
import { OpportunityAnalysisService } from './OpportunityAnalysisService';
import type { IOpportunityAnalysisService } from './interfaces/IOpportunityAnalysisService';
import type { Opportunity, OpportunitySearchFilters } from '@/types/opportunity';

export class DataAnalyticsRobot {
  private opportunityService: IOpportunityAnalysisService;
  private isRunning: boolean = false;
  private intervalId: NodeJS.Timeout | null = null;

  constructor(opportunityService?: IOpportunityAnalysisService) {
    this.opportunityService = opportunityService || new OpportunityAnalysisService();
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      console.log('🤖 DataAnalyticsRobot is already running');
      return;
    }

    try {
      console.log('🤖 Starting DataAnalyticsRobot...');
      
      // Initialize the opportunity service
      await this.opportunityService.initialize();
      
      this.isRunning = true;
      
      // Start periodic analysis
      this.startPeriodicAnalysis();
      
      console.log('✅ DataAnalyticsRobot started successfully');
      
    } catch (error) {
      console.error('❌ Failed to start DataAnalyticsRobot:', error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (!this.isRunning) {
      console.log('🤖 DataAnalyticsRobot is not running');
      return;
    }

    console.log('🤖 Stopping DataAnalyticsRobot...');
    
    this.isRunning = false;
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    console.log('✅ DataAnalyticsRobot stopped');
  }

  async analyzeTrends(): Promise<{
    trending: Opportunity[];
    analysis: {
      totalOpportunities: number;
      averageScore: number;
      topCategories: string[];
      riskDistribution: Record<string, number>;
    };
  }> {
    if (!this.isRunning) {
      throw new Error('DataAnalyticsRobot is not running. Call start() first.');
    }

    console.log('🔍 Analyzing current trends...');
    
    try {
      const trending = await this.opportunityService.getTrendingOpportunities(20);
      
      const analysis = {
        totalOpportunities: trending.length,
        averageScore: trending.reduce((sum, opp) => sum + opp.score, 0) / trending.length || 0,
        topCategories: this.getTopCategories(trending),
        riskDistribution: this.getRiskDistribution(trending)
      };
      
      console.log(`✅ Analysis complete: ${trending.length} opportunities analyzed`);
      
      return { trending, analysis };
      
    } catch (error) {
      console.error('❌ Trend analysis failed:', error);
      throw error;
    }
  }

  async searchAndAnalyze(query: string, filters?: OpportunitySearchFilters): Promise<{
    results: Opportunity[];
    insights: {
      relevanceScore: number;
      matchCount: number;
      recommendedActions: string[];
    };
  }> {
    if (!this.isRunning) {
      throw new Error('DataAnalyticsRobot is not running. Call start() first.');
    }

    console.log(`🔍 Searching and analyzing: "${query}"`);
    
    try {
      const results = await this.opportunityService.searchOpportunities(query, filters);
      
      const insights = {
        relevanceScore: results.reduce((sum, opp) => sum + opp.score, 0) / results.length || 0,
        matchCount: results.length,
        recommendedActions: this.generateRecommendations(results, query)
      };
      
      console.log(`✅ Search analysis complete: ${results.length} matches found`);
      
      return { results, insights };
      
    } catch (error) {
      console.error('❌ Search analysis failed:', error);
      throw error;
    }
  }

  getStatus(): {
    isRunning: boolean;
    serviceHealth: ReturnType<IOpportunityAnalysisService['getHealthStatus']>;
    uptime: number;
  } {
    return {
      isRunning: this.isRunning,
      serviceHealth: this.opportunityService.getHealthStatus(),
      uptime: this.isRunning ? Date.now() : 0
    };
  }

  private startPeriodicAnalysis(): void {
    // Run analysis every 10 minutes
    this.intervalId = setInterval(async () => {
      try {
        console.log('🔄 Running periodic trend analysis...');
        const { analysis } = await this.analyzeTrends();
        
        // Log key insights
        console.log(`📊 Periodic Analysis Results:
          - Total Opportunities: ${analysis.totalOpportunities}
          - Average Score: ${analysis.averageScore.toFixed(2)}
          - Top Categories: ${analysis.topCategories.join(', ')}
        `);
        
      } catch (error) {
        console.error('❌ Periodic analysis failed:', error);
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  private getTopCategories(opportunities: Opportunity[]): string[] {
    const categoryCount = opportunities.reduce((acc, opp) => {
      acc[opp.category] = (acc[opp.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(categoryCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);
  }

  private getRiskDistribution(opportunities: Opportunity[]): Record<string, number> {
    const riskCount = opportunities.reduce((acc, opp) => {
      const risk = opp.metadata.riskLevel;
      acc[risk] = (acc[risk] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = opportunities.length;
    return Object.entries(riskCount).reduce((acc, [risk, count]) => {
      acc[risk] = Math.round((count / total) * 100);
      return acc;
    }, {} as Record<string, number>);
  }

  private generateRecommendations(opportunities: Opportunity[], query: string): string[] {
    const recommendations: string[] = [];
    
    if (opportunities.length === 0) {
      recommendations.push(`No opportunities found for "${query}". Try broader search terms.`);
      return recommendations;
    }

    const avgScore = opportunities.reduce((sum, opp) => sum + opp.score, 0) / opportunities.length;
    
    if (avgScore > 0.8) {
      recommendations.push('High-quality opportunities detected. Consider immediate evaluation.');
    } else if (avgScore > 0.6) {
      recommendations.push('Moderate opportunities found. Perform additional due diligence.');
    } else {
      recommendations.push('Lower confidence results. Consider refining search criteria.');
    }

    const highRiskCount = opportunities.filter(opp => opp.metadata.riskLevel === 'high').length;
    if (highRiskCount > opportunities.length * 0.5) {
      recommendations.push('High concentration of risky opportunities. Review risk tolerance.');
    }

    const recentCount = opportunities.filter(opp => {
      const created = new Date(opp.createdAt);
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return created > dayAgo;
    }).length;

    if (recentCount > 0) {
      recommendations.push(`${recentCount} recent opportunities found. Monitor for developing trends.`);
    }

    return recommendations;
  }
}

// Export singleton instance
export const dataAnalyticsRobot = new DataAnalyticsRobot();
